import type {
  ActorProbability,
  AttackCategoryData,
  AttackType,
  OrganizationSize,
  RiskAnalysisBySession,
  RiskAnalysisItem,
  RiskConfiguration,
  RiskMatrixData,
  SectorAnalysis,
} from "@/types/risk-analysis";
import type { Sector } from "@/types/risk-analysis";
import { calculateActorTotals, determineCyFunLevel } from "./risk-calculator";

/**
 * Map entity_size from backend to OrganizationSize
 * Handles both "MICĂ"/"MICRO"/"MIC" -> "S", "MIJLOCIE" -> "M", "MARE" -> "L"
 * Returns null if entitySize is missing or invalid - callers must handle this case
 */
export function mapEntitySizeToOrganizationSize(
  entitySize: string | null | undefined
): OrganizationSize | null {
  if (!entitySize) return null;

  const normalized = entitySize.toUpperCase().trim();
  if (normalized === "MICĂ" || normalized === "MICRO" || normalized === "MIC") {
    return "S";
  }
  if (normalized === "MIJLOCIE") {
    return "M";
  }
  if (normalized === "MARE") {
    return "L";
  }

  return null;
}

/**
 * Process matrix data from server - converts RiskMatrixData to AttackCategoryData[]
 * Data is already in Romanian format - no translation needed
 *
 * @param matrixData - Matrix data from API (RiskMatrixData format)
 * @param orgSize - Organization size (not used currently but kept for consistency)
 * @returns AttackCategoryData array for UI
 */
export function processMatrixData(
  matrixData: RiskMatrixData,
  orgSize: OrganizationSize
): AttackCategoryData[] {
  // Validate that we have the risk_assessment array
  if (
    !matrixData.risk_assessment ||
    !Array.isArray(matrixData.risk_assessment)
  ) {
    console.warn("Invalid matrix data format:", matrixData);
    return [];
  }

  // Transform to AttackCategoryData structure (no value translation needed)
  return matrixData.risk_assessment.map((assessment) => {
    const normalizedCategory = assessment.attack_category.replace(
      /\s*\/\s*/g,
      "/"
    );
    return {
      categoryId: normalizedCategory,
      categoryName: normalizedCategory,
      tipAtac: assessment.nature_of_attack === 2 ? "targeted" : "global",
      impact: assessment.impact,
      actors: Object.entries(assessment.threat_actors).reduce(
        (acc, [actorId, data]) => {
          acc[actorId] = {
            probability: data.probability,
            score: data.score,
          };
          return acc;
        },
        {} as Record<string, ActorProbability>
      ),
    };
  });
}

/**
 * Transform RiskAnalysisBySession to RiskConfiguration
 */
export function transformRiskAnalysisBySessionToConfiguration(
  data: RiskAnalysisBySession,
  sessionId: string
): RiskConfiguration {
  const organizationSize = mapEntitySizeToOrganizationSize(data.entity_size);
  const selectedSectors = data.risk_analyses.map((ra) => ra.sector_id);

  return {
    id: sessionId, // Use session_id as id
    sessionId: data.session_id,
    companyName: data.nume_entitate,
    cui: data.cui,
    organizationSize,
    selectedSectors,
    source: "existing_analysis",
    createdAt: new Date(data.created_at),
  };
}

/**
 * Transform RiskAnalysisItem to SectorAnalysis
 * Reconstructs SectorAnalysis from stored matrix data
 * Handles both UI format (attackMatrix) and server format (risk_assessment)
 */
export function transformRiskAnalysisItemToSectorAnalysis(
  item: RiskAnalysisItem,
  sectors: Sector[],
  orgSize: OrganizationSize
): SectorAnalysis | null {
  const sector = sectors.find((s) => s.id === item.sector_id);
  if (!sector) {
    console.warn(`Sector ${item.sector_id} not found in sectors list`);
    return null;
  }

  // Backend always returns defaults when matrix is empty, so matrix should always have data
  // If no matrix data exists, something went wrong
  const hasMatrixData =
    item.matrix &&
    typeof item.matrix === "object" &&
    Object.keys(item.matrix).length > 0;

  if (!hasMatrixData) {
    return null;
  }

  // Process matrix data (handles both UI and server formats, including defaults)
  const attackMatrix = processMatrixData(item.matrix, orgSize);

  // Recalculate totals from processed matrix
  const actorTotals = calculateActorTotals(attackMatrix);
  const totalScore = Object.values(actorTotals).reduce(
    (sum: number, val: number) => sum + val,
    0
  );

  return {
    riskAnalysisId: item.id,
    sectorId: item.sector_id,
    sectorCode: sector.cod,
    sectorName: sector.nume,
    attackMatrix,
    actorTotals,
    totalScore: item.score || totalScore,
    cyfunLevel:
      (item.level as SectorAnalysis["cyfunLevel"]) ||
      determineCyFunLevel(totalScore),
    isComplete: item.has_populated_matrix || false,
  };
}

/**
 * Transform array of RiskAnalysisItem to Record<string, SectorAnalysis>
 */
export function transformRiskAnalysisItemsToSectorAnalyses(
  items: RiskAnalysisItem[],
  sectors: Sector[],
  orgSize: OrganizationSize
): Record<string, SectorAnalysis> {
  const sectorAnalyses: Record<string, SectorAnalysis> = {};

  for (const item of items) {
    const analysis = transformRiskAnalysisItemToSectorAnalysis(
      item,
      sectors,
      orgSize
    );
    if (analysis) {
      sectorAnalyses[item.sector_id] = analysis;
    }
  }

  return sectorAnalyses;
}
